import type { FrameworkComponent, SrmModule, SrmProject } from '@types';
import { dashboardCountry } from '../utils/clientDashboardCountries';

const WORKFLOW_SERVICES: Record<string, string> = {
    facility_risk_assessment: 'S-01', facility_security_assessment: 'S-01',
    facility_security_assessment_tool: 'S-01', s01_fsat: 'S-01',
    facility_asset_protection: 'S-02', s02_facility_plan: 'S-02',
    staff_protection_plan: 'S-03', ep_threat_analysis: 'S-03', s03_staff_protection_plan: 'S-03',
    event_security_plan: 'S-04', s04_event_security_plan: 'S-04',
    country_geopolitical_analysis: 'S-05', s05_country_intelligence: 'S-05',
    threat_group_network_analysis: 'S-06', s06_threat_group_briefing: 'S-06',
    daily_micro_report: 'S-07', s07_daily_intelligence: 'S-07',
    project_planner: 'S-10', ep_advance_site_survey: 'S-11',
    pre_movement_intelligence: 'S-12', s12_pre_movement_intelligence: 'S-12',
    crisis_management_plan: 'S-13', crisis_management: 'S-13', s13_crisis_management: 'S-13',
    business_resiliency_framework: 'S-14', business_resiliency: 'S-14', s14_business_resiliency: 'S-14',
    hsse_compliance_planner: 'S-15', hsse_compliance_briefing: 'S-15', s15_hsse_compliance: 'S-15',
    emergency_preparedness_plan: 'S-16', emergency_preparedness: 'S-16', s16_emergency_preparedness: 'S-16',
    trm_brief: 'TRM-01',
};

export type FrameworkRequestContext = {
    projectId: string;
    assetName: string;
    address: string;
    country: string;
    description: string;
    serviceCode: string;
};

export function frameworkRequestContext(project: SrmProject, module?: SrmModule | FrameworkComponent,
    action?: string): FrameworkRequestContext {
    const prefill = (module as SrmModule | undefined)?.prefillData || {};
    const assetName = project.locationName || prefill.facilityName || prefill.fapp_facility_name || project.name || project.projectName || '';
    const address = project.locationAddress || prefill.location_address || prefill.fapp_address_address || prefill.address || '';
    const countryValue = project.country || project.iso3 || prefill.location_country || prefill.fapp_address_country || prefill.country || '';
    const country = dashboardCountry(countryValue)?.name || countryValue;
    const workflow = (module as SrmModule | undefined)?.workflowId || (module as FrameworkComponent | undefined)?.type || '';
    return { projectId: project.id, assetName, address, country, serviceCode: WORKFLOW_SERVICES[workflow] || '',
        description: [
            `Framework: ${project.name || project.projectName || project.id} (${project.id})`,
            `Asset: ${assetName}${project.assetId ? ` (${project.assetId})` : ''}`,
            address && `Address: ${address}`, country && `Country: ${country}`,
            module && `Framework item: ${module.title}`,
            `Requested work: ${action || `Complete ${module?.title || 'framework item'}`}`,
        ].filter(Boolean).join('\n') };
}

const contextKey = (organizationId: string, id: string) => `framework_service_request:${organizationId}:${id}`;

export function storeFrameworkRequest(storage: Storage, organizationId: string, context: FrameworkRequestContext,
    id: string = crypto.randomUUID()): string {
    storage.setItem(contextKey(organizationId, id), JSON.stringify(context));
    return id;
}

export function readFrameworkRequest(storage: Storage, organizationId: string, id: string): FrameworkRequestContext | null {
    const value = storage.getItem(contextKey(organizationId, id));
    if (!value)
        return null;
    try {
        const context = JSON.parse(value);
        return context && ['projectId', 'assetName', 'address', 'country', 'description', 'serviceCode']
            .every(key => typeof context[key] === 'string') ? context : null;
    } catch {
        return null;
    }
}
